
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { MapPinIcon, XMarkIcon, GavelIcon, PhoneIcon } from './Icons';
import ImageWithFallback from './ImageWithFallback';
import type { Product } from '../types';

/*
================================================================================
REM 000 — THIS COMPONENT IS A SHAIF CODING STYLE (SCS) VERSION 1.0 SOFTWARE
PRODUCT MAP VISUALIZATION FOR AGRICONNECT.
================================================================================
*/

const ProductMap: React.FC = () => {
  const { products, userLocation } = useAppStore();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const navigate = useNavigate();

  // Categories for filter
  const categories = ['All', 'Vegetables', 'Fruits', 'Dairy & Eggs', 'Bakery', 'Pantry'];

  /*
  ================================================================================
  REM 002 — STYLING ZONE A. THE MAP VIEWPORT AND CONTAINER.
  ================================================================================
  */
  const stylesA = {
    container: "relative w-full h-[calc(100vh-180px)] bg-app-bg rounded-3xl overflow-hidden shadow-inner border-4 border-card-bg",
    grid: "absolute inset-0 opacity-20 pointer-events-none",
    marker: "absolute transition-all duration-300 transform hover:scale-125 cursor-pointer z-10",
    markerDot: "w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white"
  };

  /*
  ================================================================================
  REM 004 — BEHAVIOR ZONE A. COORDINATE MAPPING LOGIC.
  ================================================================================
  */
  const mapExtent = { latMin: 5.5, latMax: 6.0, lngMin: -55.5, lngMax: -55.0 };
  
  const getPosition = (lat: number = 5.8, lng: number = -55.2) => {
    const top = ((mapExtent.latMax - lat) / (mapExtent.latMax - mapExtent.latMin)) * 100;
    const left = ((lng - mapExtent.lngMin) / (mapExtent.lngMax - mapExtent.lngMin)) * 100;
    return { top: `${Math.max(5, Math.min(95, top))}%`, left: `${Math.max(5, Math.min(95, left))}%` };
  };

  const filteredProducts = useMemo(() => {
    return products.filter(p => (activeCategory === 'All' || p.category === activeCategory) && p.isActive);
  }, [products, activeCategory]);

  /*
  ================================================================================
  REM 006 — STYLING ZONE B. MAP CONTROLS AND OVERLAYS.
  ================================================================================
  */
  const stylesB = {
    overlay: "absolute top-6 left-6 right-6 flex flex-wrap gap-2 z-20",
    pill: "px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-md border-2",
    pillActive: "bg-btn-primary text-btn-primary-text border-btn-primary",
    pillInactive: "bg-card-bg text-nav-text border-transparent hover:border-brand-green/30"
  };

  /*
  ================================================================================
  REM 008 — BEHAVIOR ZONE B. INTERACTION HANDLERS.
  ================================================================================
  */
  const handleMarkerClick = (p: Product) => setSelectedProduct(p);

  /*
  ================================================================================
  REM 010 — STYLING ZONE C. PRODUCT PEEK PANEL.
  ================================================================================
  */
  const stylesC = {
    panel: "absolute bottom-6 left-6 right-6 bg-card-bg rounded-3xl p-4 shadow-2xl flex items-center gap-4 animate-in slide-in-from-bottom-10 duration-500 z-30 border-2 border-card-border",
    closeBtn: "absolute -top-2 -right-2 bg-btn-primary text-btn-primary-text p-1 rounded-full shadow-lg"
  };

  /*
  ================================================================================
  REM 012 — BEHAVIOR ZONE C. PANEL ACTIONS.
  ================================================================================
  */
  const handleContact = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedProduct) return;
    const phone = selectedProduct.seller.contact.replace(/\D/g, '');
    window.location.href = `https://wa.me/${phone}`;
  };

  /*
  ================================================================================
  REM 014 — STYLING ZONE D. (RESERVED FOR LEGEND).
  ================================================================================
  */
  const stylesD = "hidden md:block absolute bottom-6 left-6 text-[8px] font-black text-app-text-secondary uppercase tracking-[0.3em]";

  /*
  ================================================================================
  REM 016 — BEHAVIOR ZONE D. (RESERVED FOR ANALYTICS).
  ================================================================================
  */

  /*
  ================================================================================
  REM 018 — CONTENT ZONE A. MAP CANVAS RENDERING.
  ================================================================================
  */
  return (
    <div className="space-y-6 animate-in fade-in duration-700">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-black text-brand-green tracking-tight">Farm Locator</h2>
          <p className="text-app-text-secondary font-bold uppercase text-[10px] tracking-widest">Geographic Harvest Discovery</p>
        </div>
        <button onClick={() => navigate('/')} className="text-xs font-black uppercase tracking-widest text-brand-green border-b-2 border-brand-green">List View</button>
      </div>

      <section className={stylesA.container}>
        {/* Grid Background */}
        <div className={stylesA.grid} style={{ backgroundImage: 'radial-gradient(var(--color-brand-green) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

        {/* User Marker */}
        {userLocation && (
          <div className="absolute z-20 pointer-events-none" style={getPosition(userLocation.lat, userLocation.lng)}>
             <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center animate-pulse">
                <div className="w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow-lg" />
             </div>
          </div>
        )}

        {/* Product Markers */}
        {filteredProducts.map(p => (
          <div 
            key={p.id} 
            className={stylesA.marker} 
            style={getPosition(p.latitude, p.longitude)}
            onClick={() => handleMarkerClick(p)}
          >
            <div className={`${stylesA.markerDot} ${p.isAuction ? 'bg-brand-yellow text-brand-brown' : 'bg-brand-green text-white'}`}>
              <MapPinIcon className="w-4 h-4" />
            </div>
            {selectedProduct?.id === p.id && (
               <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-brand-brown text-white text-[8px] px-2 py-1 rounded font-black whitespace-nowrap shadow-xl">
                 {p.name}
               </div>
            )}
          </div>
        ))}

        {/* =========================================================
        REM 012 — CONTENT ZONE B. MAP CONTROLS.
        ========================================================= */}
        <section className={stylesB.overlay}>
          {categories.map(cat => (
            <button 
              key={cat} 
              onClick={() => setActiveCategory(cat)}
              className={`${stylesB.pill} ${activeCategory === cat ? stylesB.pillActive : stylesB.pillInactive}`}
            >
              {cat}
            </button>
          ))}
        </section>

        {/* =========================================================
        REM 030 — CONTENT ZONE C. PANEL RENDERING.
        ========================================================= */}
        {selectedProduct && (
          <section className={stylesC.panel}>
            <button onClick={() => setSelectedProduct(null)} className={stylesC.closeBtn}>
              <XMarkIcon className="w-4 h-4" />
            </button>
            <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 bg-brand-cream border-2 border-card-border shadow-sm">
               <ImageWithFallback src={selectedProduct.imageUrl} alt={selectedProduct.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-grow min-w-0">
               <h3 className="font-black text-brand-green truncate">{selectedProduct.name}</h3>
               <p className="text-[10px] font-black text-app-text-secondary uppercase tracking-widest">${selectedProduct.price.toFixed(2)} / {selectedProduct.unit}</p>
               <div className="flex gap-2 mt-2">
                 <button 
                  onClick={handleContact}
                  className="bg-[#25D366] text-white p-2 rounded-xl shadow-md hover:scale-105 transition-transform"
                 >
                    <PhoneIcon className="w-4 h-4" />
                 </button>
                 <button 
                  onClick={() => navigate(`/products/${selectedProduct.id}`)}
                  className="bg-btn-primary text-btn-primary-text text-[8px] font-black uppercase tracking-widest px-4 py-2 rounded-xl shadow-md flex-grow"
                 >
                    Details
                 </button>
               </div>
            </div>
          </section>
        )}

        <div className={stylesD}>
          Paramaribo Grid v1.0.4 • 5.8N 55.2W • AgriConnect Infrastructure
        </div>
      </section>
    </div>
  );
};

export default ProductMap;
